<template>
  <div class="order-details-wrapper">
    <button class="btn btn-light back-button-global" @click="goBack">
      <i class="bi bi-arrow-left"></i>
      <span>Retour</span>
    </button>
    <header class="page-header fade-in">
      <div>
        <h1>Détails de la commande</h1>
        <p v-if="orderData.reference">Référence {{ orderData.reference }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="convertOrderToSale">
          <i class="bi bi-cash-coin"></i>
          <span>Convertir en vente</span>
        </button>
        <button class="btn btn-secondary" @click="editOrder">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="deleteOrder">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </header>

    <section v-if="!loading && hasOrder" class="page-body fade-in">
      <div class="top-section">
        <div class="summary-card">
          <div class="summary-header">
            <h2>{{ normalizedOrder.customerFullName }}</h2>
            <template v-if="normalizedOrder.status">
              <span
                class="badge"
                :class="`badge--${statusClass(normalizedOrder.status)}`"
              >
                {{ normalizedOrder.status }}
              </span>
            </template>
            <!-- <template v-else-if="normalizedOrder.validated !== null">
              <span
                class="badge"
                :class="
                  normalizedOrder.validated
                    ? 'badge--completed'
                    : 'badge--pending'
                "
              >
                {{ normalizedOrder.validated ? "Validée" : "En attente" }}
              </span>
            </template>
            <template v-else>
              <span class="badge badge--processing">Statut inconnu</span>
            </template> -->
          </div>
          <p class="summary-customer">
            {{
              normalizedOrder.customerFullName ||
              normalizedOrder.customerReference ||
              "Client inconnu"
            }}
          </p>
          <div class="summary-grid">
            <div>
              <span class="label">Montant</span>
              <span class="value">{{
                formatCurrency(normalizedOrder.totalPriceTTC)
              }}</span>
            </div>
            <div>
              <span class="label">Quantité totale</span>
              <span class="value">{{
                formatQuantity(normalizedOrder.totalQuantity)
              }}</span>
            </div>
            <div>
              <span class="label">Montant payé</span>
              <span class="value">{{
                formatCurrency(normalizedOrder.amountPaid)
              }}</span>
            </div>
            <div>
              <span class="label">Restant dû</span>
              <span class="value">{{
                formatCurrency(normalizedOrder.amountUnpaid)
              }}</span>
            </div>
            <div>
              <span class="label">Date commande</span>
              <span class="value">{{ formatDate(normalizedOrder.date) }}</span>
            </div>
            <div>
              <span class="label">Dernière mise à jour</span>
              <span class="value">{{
                formatDate(normalizedOrder.updated_at)
              }}</span>
            </div>
            <div v-if="normalizedOrder.user?.name">
              <span class="label">Saisie par</span>
              <span class="value">{{ normalizedOrder.user.name }}</span>
            </div>
          </div>
        </div>

        <div class="notes-card" v-if="normalizedOrder.remark">
          <h3>Remarque</h3>
          <p>{{ normalizedOrder.remark }}</p>
        </div>

        <div class="notes-card" v-if="normalizedOrder.deliveryAddress">
          <h3>Adresse de livraison</h3>
          <p>{{ normalizedOrder.deliveryAddress }}</p>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-card">
          <h3>Informations client</h3>
          <div class="info-line">
            <span class="label">Nom</span>
            <span class="value">
              {{
                normalizedOrder.customerFullName ||
                normalizedOrder.customer?.fullName ||
                "—"
              }}
            </span>
          </div>
          <div class="info-line">
            <span class="label">Référence client</span>
            <span class="value">{{
              normalizedOrder.customerReference || "—"
            }}</span>
          </div>
          <div class="info-line" v-if="normalizedOrder.customer?.phone">
            <span class="label">Téléphone</span>
            <span class="value">{{ normalizedOrder.customer.phone }}</span>
          </div>
          <div class="info-line" v-if="normalizedOrder.customer?.society">
            <span class="label">Société</span>
            <span class="value">{{ normalizedOrder.customer.society }}</span>
          </div>
          <div
            class="info-line"
            v-if="
              normalizedOrder.customer?.address ||
              normalizedOrder.customer?.city
            "
          >
            <span class="label">Adresse</span>
            <span class="value">
              {{
                [
                  normalizedOrder.customer?.address,
                  normalizedOrder.customer?.zipCode,
                  normalizedOrder.customer?.city,
                ]
                  .filter(Boolean)
                  .join(", ") || "—"
              }}
            </span>
          </div>
        </div>

        <div class="info-card">
          <h3>État de paiement</h3>
          <div class="info-line">
            <span class="label">Statut</span>
            <span class="value">{{
              normalizedOrder.paymentStatus || "—"
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">Montant payé</span>
            <span class="value">{{
              formatCurrency(normalizedOrder.amountPaid)
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">Restant dû</span>
            <span class="value">{{
              formatCurrency(normalizedOrder.amountUnpaid)
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">Montant TTC</span>
            <span class="value">{{
              formatCurrency(normalizedOrder.totalPriceTTC)
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">Montant HT</span>
            <span class="value">{{
              formatCurrency(normalizedOrder.totalPriceHT)
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">TVA totale</span>
            <span class="value">{{
              formatCurrency(normalizedOrder.totalTVA)
            }}</span>
          </div>
        </div>
      </div>

      <div class="items-card" v-if="normalizedOrder.items.length">
        <h3>Articles</h3>
        <div class="items-list">
          <div
            class="item-row"
            v-for="item in normalizedOrder.items"
            :key="item.id"
          >
            <div class="item-row-header">
              <div class="item-title">
                <h4>{{ item.name }}</h4>
                <p class="item-reference">{{ item.reference }}</p>
              </div>
              <div class="item-stats">
                <span class="item-chip">
                  {{ formatQuantity(item.quantity) }}
                  <small v-if="item.measuring">{{ item.measuring }}</small>
                </span>
                <span class="item-chip item-chip--price">
                  {{ formatCurrency(item.priceTTC) }}/U
                </span>
                <span class="item-chip item-chip--total">
                  {{ formatCurrency(item.totalPriceTTC) }}
                </span>
              </div>
            </div>
            <div class="item-row-footer">
              <div class="item-meta">
                <span v-if="item.remark">
                  <i class="bi bi-chat-square-text"></i>
                  {{ item.remark }}
                </span>
                <span v-else class="item-muted">Aucune remarque</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Chargement de la commande...</p>
    </div>

    <div v-else class="empty-state">
      <i class="bi bi-emoji-frown"></i>
      <h3>Commande introuvable</h3>
      <p>La commande demandée semble ne plus exister.</p>
      <button class="btn btn-primary" @click="goToList">
        <i class="bi bi-receipt"></i>
        <span>Voir les commandes</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OrderShow",
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapGetters("sellerOrder", {
      order: "getSellerOrder",
    }),
    reference() {
      return this.$route.params.reference;
    },
    orderData() {
      return this.order || {};
    },
    hasOrder() {
      return Object.keys(this.orderData).length > 0;
    },
    normalizedOrder() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };

      if (!this.hasOrder) {
        return {
          items: [],
        };
      }

      const rawCustomer =
        this.orderData.seller_customer || this.orderData.customer || {};

      const items = Array.isArray(this.orderData.order_items)
        ? this.orderData.order_items.map((entry) => {
            const itemInfo = entry.item || {};
            return {
              id: entry.id || entry.uuid || entry.item_reference,
              reference: entry.item_reference || itemInfo.reference || "—",
              name:
                itemInfo.fullName ||
                itemInfo.name ||
                entry.item_full_name ||
                entry.itemName ||
                entry.item_reference ||
                "Article",
              quantity: toNumber(entry.quantity, 0),
              priceTTC: toNumber(
                entry.priceTTC != null ? entry.priceTTC : itemInfo.priceTTC,
                0
              ),
              totalPriceTTC: toNumber(
                entry.totalPriceTTC != null
                  ? entry.totalPriceTTC
                  : entry.total_price_ttc,
                toNumber(entry.priceTTC, 0) * toNumber(entry.quantity, 0)
              ),
              remark: entry.remark || entry.note || "",
              measuring:
                itemInfo.measuring ||
                itemInfo.unit ||
                itemInfo.unit_label ||
                null,
            };
          })
        : [];

      const totalPriceTTC = toNumber(
        this.orderData.totalPriceTTC != null
          ? this.orderData.totalPriceTTC
          : this.orderData.total_price_ttc != null
          ? this.orderData.total_price_ttc
          : this.orderData.totalPriceTTCValue,
        0
      );
      const totalPriceHT = toNumber(
        this.orderData.totalPriceHT != null
          ? this.orderData.totalPriceHT
          : this.orderData.total_price_ht,
        0
      );
      const totalTVA = toNumber(
        this.orderData.totalTVA != null
          ? this.orderData.totalTVA
          : this.orderData.total_tva,
        0
      );
      const totalQuantity = toNumber(
        this.orderData.totalQuantity != null
          ? this.orderData.totalQuantity
          : this.orderData.total_quantity,
        0
      );
      const amountPaid = toNumber(
        this.orderData.amountPaid != null
          ? this.orderData.amountPaid
          : this.orderData.amount_paid != null
          ? this.orderData.amount_paid
          : this.orderData.payrollAmount,
        0
      );

      const amountUnpaid = toNumber(
        this.orderData.amountUnpaid != null
          ? this.orderData.amountUnpaid
          : this.orderData.amount_unpaid != null
          ? this.orderData.amount_unpaid
          : this.orderData.remaining_amount != null
          ? this.orderData.remaining_amount
          : totalPriceTTC - amountPaid,
        0
      );

      const validatedRaw = this.orderData.validated;
      const hasValidated = validatedRaw !== null && validatedRaw !== undefined;

      return {
        id: this.orderData.id,
        reference: this.orderData.reference,
        status: this.orderData.status || null,
        validated: hasValidated ? Boolean(validatedRaw) : null,
        customerFullName:
          this.orderData.customerFullName ||
          rawCustomer.fullName ||
          rawCustomer.name ||
          null,
        customerReference:
          this.orderData.seller_customer_reference ||
          rawCustomer.reference ||
          null,
        customer: rawCustomer,
        user: this.orderData.user || null,
        date:
          this.orderData.date ||
          this.orderData.created_at ||
          this.orderData.order_date ||
          null,
        updated_at: this.orderData.updated_at || null,
        remark: this.orderData.remark || null,
        deliveryAddress:
          this.orderData.addressLaivraison ||
          this.orderData.delivery_address ||
          null,
        totalPriceTTC,
        totalPriceHT,
        totalTVA,
        totalQuantity,
        amountPaid,
        amountUnpaid,
        paymentStatus: this.orderData.payment_status || null,
        items,
      };
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    goToList() {
      this.$router.push({ name: "orders-list" });
    },
    editOrder() {
      this.$router.push({
        name: "orders-edit",
        params: { reference: this.reference },
      });
    },
    async convertOrderToSale() {
      const confirmed = await this.$confirm({
        title: "Convertir en vente",
        message:
          "Voulez-vous convertir cette commande en vente ? Cette action est irréversible.",
        confirmButtonText: "Convertir",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;

      try {
        await this.$store.dispatch("sellerOrder/convertToSale", this.reference);
        this.$toast.success("Commande convertie en vente avec succès.");
        await this.loadOrder();
      } catch (error) {
        console.error("Error converting order to sale", error);
        this.$toast.error("Impossible de convertir la commande en vente.");
      }
    },
    async deleteOrder() {
      const confirmed = await this.$confirm({
        title: "Supprimer la commande",
        message: "Êtes-vous sûr de vouloir supprimer cette commande ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("sellerOrder/destroy", this.reference);
        this.$toast.success("Commande supprimée avec succès.");
        this.goToList();
      } catch (error) {
        console.error("Error deleting order", error);
        this.$toast.error("Impossible de supprimer la commande.");
      }
    },
    statusClass(status = "") {
      return status.toLowerCase().replace(/\s+/g, "-");
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (error) {
        return value;
      }
    },
    formatQuantity(value) {
      if (!value && value !== 0) return "—";
      const number = Number(value);
      if (Number.isNaN(number)) return value;
      return number % 1 === 0 ? number.toString() : number.toFixed(2);
    },
    async loadOrder() {
      this.loading = true;
      try {
        await this.$store.dispatch("sellerOrder/show", this.reference);
      } catch (error) {
        console.error("Error fetching order", error);
        this.$toast.error("Impossible de récupérer la commande.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadOrder();
  },
};
</script>

<style scoped>
.order-details-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 5rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.top-section {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 1.5rem;
}

.summary-card {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.08),
    rgba(147, 51, 234, 0.08)
  );
  border-radius: 20px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.summary-header h2 {
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
}

.badge {
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(148, 163, 184, 0.2);
  color: #475569;
}

.badge--pending {
  background: rgba(251, 191, 36, 0.18);
  color: #92400e;
}

.badge--processing {
  background: rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
}

.badge--completed {
  background: rgba(34, 197, 94, 0.18);
  color: #166534;
}

.badge--cancelled {
  background: rgba(239, 68, 68, 0.18);
  color: #991b1b;
}

.summary-customer {
  margin: 0;
  color: #475569;
  font-size: 1rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.summary-grid .label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.summary-grid .value {
  display: block;
  font-weight: 600;
  color: #0f172a;
}

.notes-card {
  background: #f8faff;
  border-radius: 20px;
  padding: 1.5rem;
  color: #374151;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notes-card h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #111827;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.06);
}

.info-card h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #0f172a;
}

.info-line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: #475569;
}

.info-line .label {
  font-weight: 600;
  color: #64748b;
}

.info-line .value {
  font-weight: 600;
  color: #111827;
}

.items-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.items-card h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.item-row {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.05),
    rgba(147, 51, 234, 0.06)
  );
  border-radius: 18px;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.item-row-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.item-title h4 {
  margin: 0;
  font-size: 1.05rem;
  color: #0f172a;
}

.item-reference {
  margin: 0.1rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.item-stats {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.item-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  font-weight: 600;
  font-size: 0.9rem;
}

.item-chip small {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
}

.item-chip--price {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.item-chip--total {
  background: rgba(16, 185, 129, 0.15);
  color: #0f766e;
}

.item-row-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
}

.item-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.item-meta i {
  color: #2563eb;
}

.item-muted {
  color: #94a3b8;
  font-style: italic;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #2563eb;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.back-button-global {
  position: fixed;
  top: 1.25rem;
  left: 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
  z-index: 3000;
}

.back-button-global:hover {
  transform: translateY(-1px);
}

.btn-secondary {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .order-details-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .top-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .order-details-wrapper {
    padding: 4.5rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .back-button-global {
    top: 0.75rem;
    left: 0.75rem;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  }
  .back-button-global:hover {
    transform: none;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .summary-card {
    padding: 1.25rem;
  }
}
</style>
